import type { ComponentProps } from "react";

import type { ColumnsType } from "@similarweb/pro-logic-components";
import { Table } from "@similarweb/pro-logic-components";

import { translate } from "app/filters/i18nFilter";
import type { ISortProps } from "app/pages/digital-marketing/ad-intelligence/common/utils/utils";

import type { ColumnGenerator, ITransformedEnrichedKeywordsData, ITransformedEnrichedPagesData } from "../types";

const DEFAULT_PAGE_SIZE = 5;
export const initialTableSort: ISortProps = { asc: false, sort: "ClicksShare" };

export interface IEnrichedTableProps<T> {
    data: T[];
    selectedPageTab: string;
    getTableColumns: ColumnGenerator;
    currentPage: number;
    setCurrentPage: (page: number) => void;
    isLoading: boolean;
    isError: boolean;
    totalCount: number;
    sort: ISortProps;
    onSort: (sort: ISortProps) => void;
}

export const EnrichedTable = <T extends ITransformedEnrichedPagesData | ITransformedEnrichedKeywordsData>({
    data,
    selectedPageTab,
    getTableColumns,
    currentPage,
    setCurrentPage,
    isLoading,
    isError,
    totalCount,
    sort,
    onSort,
}: IEnrichedTableProps<T>) => {
    const onPageChangeHandler = (newPage: number) => {
        setCurrentPage(newPage);
    };

    const onSortHandler: ComponentProps<typeof Table<T>>["onSort"] = (sorter) => {
        const isAsc = sorter.order === "ascend";
        onSort({
            sort: sorter.columnKey as string,
            asc: isAsc,
        });
    };

    const columns = getTableColumns(currentPage, DEFAULT_PAGE_SIZE, selectedPageTab, sort);

    const noDataObject = isError
        ? {
              title: translate("website.topics.enrich.table.error.title"),
              subtitle: translate("website.topics.enrich.table.error.subtitle"),
          }
        : {
              title: translate("website.topics.enrich.table.no.data.title"),
              subtitle: translate("website.topics.enrich.table.no.data.subtitle"),
          };

    return (
        <Table<T>
            columnConfig={columns as ColumnsType<T>}
            data={data}
            isLoading={isLoading}
            loaderRowCount={DEFAULT_PAGE_SIZE}
            noData={noDataObject}
            size="large"
            pagination={{
                pageSize: DEFAULT_PAGE_SIZE,
                total: totalCount ?? 0,
                current: currentPage,
                // eslint-disable-next-line @typescript-eslint/naming-convention
                onChange: (_, __, newPage) => onPageChangeHandler(newPage),
            }}
            sticky={false}
            onSort={onSortHandler}
        />
    );
};
